import { env } from 'constants/env';

import type { FeatureFlagName, FeatureFlagRule } from './types';
import { defaultRule } from './rules';

export class FeatureFlags {
  private static instance: FeatureFlags;

  private flags: Partial<Record<FeatureFlagName, unknown>>;
  private rules: Partial<Record<FeatureFlagName, FeatureFlagRule>>;

  private constructor(initialFlags: Partial<Record<FeatureFlagName, unknown>>) {
    this.flags = { ...initialFlags };
    this.rules = {};
  }

  public static init(
    initialFlags: Partial<Record<FeatureFlagName, unknown>> = {}
  ) {
    if (!FeatureFlags.instance) {
      FeatureFlags.instance = new FeatureFlags(initialFlags);
    }
    return FeatureFlags.instance;
  }

  private static getInstance(): FeatureFlags {
    if (!FeatureFlags.instance) {
      throw new Error('FeatureFlags not initialized. Call init() first.');
    }
    return FeatureFlags.instance;
  }

  public static override(flags: Partial<Record<FeatureFlagName, unknown>>) {
    const instance = FeatureFlags.getInstance();

    Object.assign(instance.flags, flags);
  }

  public static setRules(
    rules: Partial<Record<FeatureFlagName, FeatureFlagRule>>
  ) {
    const instance = FeatureFlags.getInstance();

    Object.assign(instance.rules, rules);
  }

  public static canView(feature: FeatureFlagName): boolean {
    const instance = FeatureFlags.getInstance();

    if (!(feature in instance.flags)) {
      if (env.clearcoverEnv !== 'production') {
        console.warn(`Feature flag "${feature}" is not defined.`);
      }
      return false;
    }

    const featureFlag = instance.flags[feature];
    const rule = instance.rules[feature] ?? defaultRule;

    return rule(featureFlag);
  }

  // Reset the singleton instance for testing purposes
  public static reset() {
    // @ts-ignore
    FeatureFlags.instance = undefined;
  }
}
